import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { User } from 'Shared/constants/types';

// Selectors
import { getAllBaInfo } from 'Shared/selectors/BaInfo.selectors';

// Actions
import * as PiIntegrationsActions from 'Shared/actions/PiIntegrations.actions';

// Hooks
import { useOrganization } from './use-organization';
import { useCurrentUser } from './use-current-user';

// Utils
import { sortUsersByName } from 'Shared/utils/sorting';

export const useFetchBulkBAData = (users: User[]) => {
  const [usersWithBA, setUsersWithBA] = useState<User[]>([]);
  const [usersWithPendingBA, setUsersWithPendingBA] = useState<User[]>([]);
  const [usersWithoutBA, setUsersWithoutBA] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { id: userId } = useCurrentUser();
  const dispatch = useDispatch();
  const { organization } = useOrganization();
  const allBaInfo = useSelector(getAllBaInfo);

  const filterBaStatus = async (user: User) => {
    const baInfo = allBaInfo.get(user.piUserGuid)?.toJS();

    if (baInfo?.hasPendingBa && !baInfo?.referencePattern?.referencePatternName) {
      setUsersWithPendingBA(prev => [...prev, user]);
    } else if (baInfo?.referencePattern?.referencePatternName) {
      setUsersWithBA(prev => [...prev, user]);
    } else {
      try {
        const response = await dispatch(
          PiIntegrationsActions.getUser({
            companyGuid: organization.pi_company_guid,
            userGuid: user.piUserGuid,
          })
        );
        const responseData = response?.data;
        if (responseData?.hasPendingBa && !responseData?.referencePattern?.referencePatternName) {
          setUsersWithPendingBA(prev => [...prev, user]);
        } else if (responseData?.referencePattern?.referencePatternName) {
          setUsersWithBA(prev => [...prev, user]);
        } else {
          setUsersWithoutBA(prev => [...prev, user]);
        }
      } catch (error) {
        console.error(`Error fetching user data for userGuid: ${user.piUserGuid}`, error);
        setUsersWithoutBA(prev => [...prev, user]);
      }
    }
  };

  const sortAndOrganizeUsers = (users: User[], currentUserId: number) => {
    const sortedUsers = sortUsersByName(users);
    const currentUserIndex = sortedUsers.findIndex(user => user.id === currentUserId);
    if (currentUserIndex > -1) {
      const [currentUser] = sortedUsers.splice(currentUserIndex, 1);
      sortedUsers.unshift(currentUser);
    }
    return sortedUsers;
  };

  useEffect(() => {
    const fetchBAData = async () => {
      setIsLoading(true);

      // Reset state before filtering users
      setUsersWithBA([]);
      setUsersWithPendingBA([]);
      setUsersWithoutBA([]);

      await Promise.all(users.map(user => filterBaStatus(user)));

      // Sort and organize users after filtering status types
      setUsersWithBA(prev => sortAndOrganizeUsers(prev, userId));
      setUsersWithPendingBA(prev => sortUsersByName(prev));
      setUsersWithoutBA(prev => sortUsersByName(prev));

      setIsLoading(false);
    };

    fetchBAData();
  }, [users]);

  return { usersWithBA, usersWithPendingBA, usersWithoutBA, isLoading };
};
